import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * CsvImportDialog — upload + preview + certify consent + commit.
 *
 * Props: open, onClose, restaurantId, onImported()
 */
export default function CsvImportDialog({ open, onClose, restaurantId, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setConsent(false); };

  const handlePreview = async (f) => {
    setFile(f);
    setLoading(true);
    try {
      const data = await api.previewCustomerCsvImport(restaurantId, f);
      setPreview(data);
    } catch (err) {
      toast.error(err.message || "No se pudo leer el CSV");
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!consent) return toast.error("Debes certificar el consentimiento de cada cliente");
    setLoading(true);
    try {
      const rows = preview?.all_rows || [];
      const r = await api.commitCustomerCsvImport(restaurantId, { rows, certify_consent: true });
      toast.success(`Importados ${r.inserted} clientes${r.skipped > 0 ? ` · ${r.skipped} duplicados omitidos` : ""}`);
      onImported?.();
      reset();
      onClose();
    } catch (err) {
      toast.error(err.message || "Error al importar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && (reset(), onClose())}>
      <DialogContent className="max-w-xl" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" /> Importar clientes desde CSV
          </DialogTitle>
          <DialogDescription>
            Sube un archivo con columnas: nombre, email, teléfono, cumpleaños, notas. Las duplicadas por email se omiten.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-10 cursor-pointer hover:border-primary transition" data-testid="csv-upload-zone">
            <Upload className="w-8 h-8 text-muted-foreground" />
            <p className="text-sm">{loading ? "Leyendo…" : "Arrastra o selecciona tu CSV"}</p>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePreview(f); }}
              data-testid="csv-file-input"
            />
          </label>
        )}

        {preview && (
          <div className="space-y-4" data-testid="csv-preview">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-3">
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{preview.valid_rows}</p>
                <p className="text-xs text-muted-foreground">Válidos</p>
              </div>
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{preview.errors?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Con errores</p>
              </div>
              <div className="rounded-lg bg-muted/50 dark:bg-slate-900/20 p-3">
                <p className="text-2xl font-bold">{preview.total_rows}</p>
                <p className="text-xs text-muted-foreground">Total leído</p>
              </div>
            </div>

            {preview.preview?.length > 0 && (
              <div className="max-h-56 overflow-y-auto rounded-lg border">
                <table className="w-full text-xs">
                  <thead className="bg-muted text-muted-foreground">
                    <tr><th className="text-left px-3 py-1.5">Nombre</th><th className="text-left px-3 py-1.5">Email</th><th className="text-left px-3 py-1.5">Tel</th><th className="text-left px-3 py-1.5">Cumple</th></tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-1.5">{r.name}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.email || "-"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.phone || "-"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.birthday || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <label className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 cursor-pointer" data-testid="csv-consent-block">
              <input
                type="checkbox"
                className="mt-1 w-4 h-4 accent-amber-600"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="csv-consent-checkbox"
              />
              <span className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                Certifico que cada cliente del archivo ha dado consentimiento explícito para el tratamiento de sus datos con fines de fidelización (RGPD art. 7). Responsabilidad legal a mi cargo.
              </span>
            </label>
          </div>
        )}

        {loading && <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
          {preview && (
            <Button onClick={commit} disabled={!consent || loading || !(preview.valid_rows > 0)} data-testid="csv-commit-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Importar {preview.valid_rows} clientes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
